// Generic report viewer (Increments R2+R3) — resolves a template from the
// registry, shows its filter bar (date range / status / payroll-run picker),
// executes the template's fetch against real endpoints and renders a table with
// a column picker and CSV export. The CSV always carries a metadata header
// block (title, generated at/by, filters, source, confidentiality) so
// downloads are self-describing.
//
// R3 adds saved definitions: "Save report" persists the current template +
// filters + visible columns to the payroll-service definitions API, and the
// `reports/saved/:savedId` route re-opens a definition with its saved filters
// and column selection rehydrated as the initial viewer state.
//
// R4 adds ENGINE mode for saved definitions WITHOUT a template_id (created in
// the guided Report Builder): instead of resolving a frontend registry
// template, the viewer loads the server-side field catalog for the
// definition's domain (getDomainFields — already permission-filtered per
// viewer) and executes through POST /reports/definitions/{id}/run, which runs
// under the VIEWER's permissions and stamps last_run_at server-side.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/lib/apiError';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { Textarea } from '@/features/shared/components/ui/textarea';
import { Switch } from '@/features/shared/components/ui/switch';
import { Checkbox } from '@/features/shared/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/features/shared/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/features/shared/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/features/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/features/shared/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/features/shared/components/ui/dropdown-menu';
import {
  ArrowLeft,
  Archive,
  ChevronDown,
  Copy,
  Download,
  Loader2,
  Save,
  Share2,
  ShieldAlert,
  SlidersHorizontal,
  ExternalLink,
  FileBarChart2,
} from 'lucide-react';
import { useAppSelector } from '@/app/hooks';
import { useRbacRoles, useHasPermission } from '@/rbac/useRbac';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { toCSV, downloadCsv } from '@/lib/csv';
import { payrollApi } from '@/features/payroll/api/payrollApi';
import type { PayrollRun } from '@/features/payroll/types';
import {
  createSavedReport,
  getSavedReport,
  updateSavedReport,
  duplicateSavedReport,
  archiveSavedReport,
  markSavedReportRan,
  type SavedReport,
  type SavedReportFilters,
  type SavedReportVisibility,
} from '@/services/savedReportService';
import {
  getDomainFields,
  runSavedReport,
  exportReport,
  openReportDownload,
  formatCell,
  type ReportDomainFields,
  type ReportEngineResult,
  type ReportExportFormat,
} from '@/services/reportEngineService';
import { EngineFilterBar, cleanFilters } from './EngineFilterBar';
import { ReportScopeNotice, ReportScopeEmptyReason } from './ReportScopeNotice';
import { EngineResultChart } from './EngineResultChart';
import { ShareReportDialog, shareRoleLabel } from './ShareReportDialog';
import { ScheduleReportDialog } from './ScheduleReportDialog';
import { CalendarClock } from 'lucide-react';
import {
  canSeeTemplate,
  getTemplate,
  type ReportResult,
  type ReportTemplate,
} from './registry';
import { recordRecent } from './storage';

const isoDay = (d: Date) => d.toISOString().slice(0, 10);

/** Default [from, to] window: negative days look back, positive look ahead. */
function defaultRange(days: number): [string, string] {
  const today = new Date();
  const other = new Date(today.getTime() + days * 24 * 60 * 60 * 1000);
  return days < 0 ? [isoDay(other), isoDay(today)] : [isoDay(today), isoDay(other)];
}

const apiErrorMessage = (e: any, fallback: string): string => getApiErrorMessage(e, fallback);

// ---- Route entry -----------------------------------------------------------------
//
// Two shapes share this page:
//   reports/:templateId       → plain template viewer
//   reports/saved/:savedId    → saved definition (loads it, then rehydrates the
//                               template viewer with the saved filters/columns)

export const ReportViewerPage: React.FC = () => {
  const { t } = useTranslation('reports');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { templateId, savedId } = useParams<{ templateId?: string; savedId?: string }>();
  const roles = useRbacRoles();

  // .../reports/... → reports base + tenant root for navigation.
  const base = pathname.slice(0, pathname.indexOf('/reports') + '/reports'.length);
  const tenantRoot = base.replace(/\/reports$/, '');

  // ---- Saved-definition loading (saved mode only) ------------------------------
  const [saved, setSaved] = useState<SavedReport | null>(null);
  const [savedLoading, setSavedLoading] = useState(!!savedId);
  const [savedError, setSavedError] = useState('');

  useEffect(() => {
    if (!savedId) {
      setSaved(null);
      setSavedLoading(false);
      setSavedError('');
      return;
    }
    let active = true;
    setSaved(null);
    setSavedLoading(true);
    setSavedError('');
    getSavedReport(savedId)
      .then((def) => active && setSaved(def))
      .catch((e: any) => {
        if (!active) return;
        setSavedError(
          e?.response?.status === 403
            ? t('saved.noAccess', 'You do not have access to this saved report.')
            : apiErrorMessage(e, t('saved.loadFailed', 'Could not load this saved report.')),
        );
      })
      .finally(() => active && setSavedLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedId]);

  const template = savedId
    ? saved?.template_id
      ? getTemplate(saved.template_id)
      : undefined
    : templateId
      ? getTemplate(templateId)
      : undefined;

  if (savedId && savedLoading) {
    return (
      <div className="py-16 flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        {t('saved.loading', 'Loading saved report…')}
      </div>
    );
  }

  if (savedId && (savedError || !saved)) {
    return (
      <NotFoundState
        title={t('saved.notFound', 'Saved report unavailable')}
        body={savedError || t('saved.notFoundBody', 'This saved report does not exist or is no longer available.')}
        backLabel={t('viewer.back', 'Back to reports')}
        onBack={() => navigate(base)}
      />
    );
  }

  // Builder-created definitions carry no template_id — run them through the
  // server-side report engine instead of a frontend registry template.
  if (savedId && saved && !saved.template_id) {
    return <EngineViewerBody key={saved.id} saved={saved} base={base} />;
  }

  if (!template) {
    return (
      <NotFoundState
        title={
          savedId
            ? t('saved.templateMissing', 'Report template no longer available')
            : t('viewer.notFound', 'Report not found')
        }
        body={
          savedId
            ? t(
                'saved.templateMissingBody',
                'The template this saved report was built from has been removed or renamed, so it can no longer run.',
              )
            : t('viewer.notFoundBody', 'This report does not exist or is no longer available.')
        }
        backLabel={t('viewer.back', 'Back to reports')}
        onBack={() => navigate(base)}
      />
    );
  }

  if (!canSeeTemplate(roles, template)) {
    return (
      <NotFoundState
        title={t('viewer.noAccess', 'No access to this report')}
        body={t('viewer.noAccessBody', 'Your role does not include the permissions required for this report.')}
        backLabel={t('viewer.back', 'Back to reports')}
        onBack={() => navigate(base)}
      />
    );
  }

  return (
    <ViewerBody
      key={saved?.id ?? template.id}
      template={template}
      saved={saved}
      base={base}
      tenantRoot={tenantRoot}
    />
  );
};

// ---- Viewer -------------------------------------------------------------------

interface ViewerBodyProps {
  template: ReportTemplate;
  /** Present in saved mode — filters/columns rehydrate from it. */
  saved: SavedReport | null;
  base: string;
  tenantRoot: string;
}

const ViewerBody: React.FC<ViewerBodyProps> = ({ template, saved, base, tenantRoot }) => {
  const { t } = useTranslation('reports');
  const navigate = useNavigate();
  const user = useAppSelector((s) => s.auth.user);
  const canCreate = useHasPermission('reports:create');
  const canShare = useHasPermission('reports:share');
  const { isTenantOwner } = useRoleAccess();
  const confirm = useConfirm();
  // P4: server export for curated engine templates (hooks before any return).
  const canServerExport = useHasPermission('reports:export') && !!template.preset;
  const [presetExporting, setPresetExporting] = useState(false);

  const hasDateRange = !!template.filters?.includes('dateRange');
  const hasStatus = !!template.filters?.includes('status');
  const needsRun = !!template.needsRunPicker && template.kind !== 'passthrough';

  const savedFilters: SavedReportFilters = saved?.filters ?? {};

  // Initial filter state: saved values win; otherwise the template defaults.
  const [initialFrom, initialTo] = useMemo(
    () => (hasDateRange ? defaultRange(template.defaultRangeDays ?? -30) : ['', '']),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [template.id],
  );
  const [from, setFrom] = useState(typeof savedFilters.from === 'string' ? savedFilters.from : initialFrom);
  const [to, setTo] = useState(typeof savedFilters.to === 'string' ? savedFilters.to : initialTo);
  const [status, setStatus] = useState(
    typeof savedFilters.status === 'string' && savedFilters.status
      ? savedFilters.status
      : template.statusOptions?.[0] ?? '',
  );

  const [runs, setRuns] = useState<PayrollRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(needsRun);
  const [runId, setRunId] = useState('');

  const [result, setResult] = useState<ReportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const recordedRef = useRef(false);
  const ranStampedRef = useRef(false);

  // Column selection: null = all columns. A saved definition's column list is
  // the initial selection; unknown keys are ignored at render time so a
  // template whose columns changed still displays sanely.
  const [selectedCols, setSelectedCols] = useState<string[] | null>(
    saved?.columns?.length ? saved.columns : null,
  );

  // Payroll run picker: newest first (listRuns sorts desc); a saved runId wins
  // when it still exists, otherwise default to the most recent non-draft run
  // (draft runs have nothing snapshot-worthy yet).
  useEffect(() => {
    if (!needsRun) return;
    let active = true;
    setRunsLoading(true);
    payrollApi
      .listRuns()
      .then((list) => {
        if (!active) return;
        setRuns(list);
        const savedRun =
          typeof savedFilters.runId === 'string' ? list.find((r) => r.id === savedFilters.runId) : undefined;
        const preferred = savedRun ?? list.find((r) => r.state !== 'draft') ?? list[0];
        if (preferred) setRunId(preferred.id);
      })
      .catch(() => active && setRuns([]))
      .finally(() => active && setRunsLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsRun]);

  const runReport = useCallback(() => {
    if (!template.fetch) return;
    if (needsRun && !runId) return;
    let active = true;
    setLoading(true);
    setError('');
    template
      .fetch({ runId: runId || undefined, from: from || undefined, to: to || undefined, status: status || undefined })
      .then((res) => {
        if (!active) return;
        setResult(res);
        if (!recordedRef.current) {
          recordedRef.current = true;
          recordRecent(template.id);
        }
        if (saved && !ranStampedRef.current) {
          ranStampedRef.current = true;
          markSavedReportRan(saved.id).catch(() => {
            /* best-effort last_run_at stamp */
          });
        }
      })
      .catch((e: any) => {
        if (!active) return;
        setResult(null);
        setError(apiErrorMessage(e, 'Failed to run report'));
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, needsRun, runId, from, to, status, saved?.id]);

  useEffect(() => {
    const cancel = runReport();
    return cancel;
  }, [runReport]);

  const tplName = (tpl: ReportTemplate) => t(`templates.${tpl.id}.name`, tpl.name);

  // ---- Column picker ------------------------------------------------------------

  const allColumns = result?.columns ?? [];
  const visibleColumns = useMemo(
    () => (selectedCols ? allColumns.filter((c) => selectedCols.includes(c.key)) : allColumns),
    [allColumns, selectedCols],
  );

  const toggleColumn = (key: string) => {
    const current = selectedCols ?? allColumns.map((c) => c.key);
    const next = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
    // Never allow hiding the last visible column — an all-blank table reads as broken.
    if (allColumns.length > 0 && allColumns.every((c) => !next.includes(c.key))) return;
    setSelectedCols(next);
  };

  /** The visible column keys, in result order — what we persist on save. */
  const visibleColumnKeys = visibleColumns.map((c) => c.key);

  // ---- Current filters (persisted on save + shown in the CSV metadata) ----------

  const currentFilters = (): SavedReportFilters => {
    const f: SavedReportFilters = {};
    if (needsRun && runId) f.runId = runId;
    if (hasDateRange) {
      if (from) f.from = from;
      if (to) f.to = to;
    }
    if (hasStatus && status) f.status = status;
    return f;
  };

  // ---- Save / saved-mode actions --------------------------------------------------

  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  const [publishTenant, setPublishTenant] = useState(false);
  const [saving, setSaving] = useState(false);

  const ownsSaved = !!saved && !!user?.id && String(saved.owner_user_id) === String(user.id);
  const canEditSaved = !!saved && (ownsSaved || isTenantOwner);

  // R6 sharing — the persisted state lives here so the badge and subsequent
  // "Save changes" calls reflect a successful share immediately.
  const [shareOpen, setShareOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const canSchedule = useHasPermission('reports:schedule');
  const [shareVisibility, setShareVisibility] = useState<SavedReportVisibility>(
    saved?.visibility ?? 'private',
  );
  const [shareRoles, setShareRoles] = useState<string[]>(saved?.shared_roles ?? []);

  /** Persists sharing only — rethrows so the dialog can surface 'share_blocked'. */
  const submitShare = async (visibility: SavedReportVisibility, roles: string[]) => {
    if (!saved) return;
    await updateSavedReport(saved.id, {
      name: saved.name,
      description: saved.description ?? undefined,
      domain: template.domain,
      template_id: template.id,
      columns: saved.columns,
      filters: saved.filters,
      visibility,
      shared_roles: visibility === 'role' ? roles : [],
      status: saved.status,
    });
    setShareVisibility(visibility);
    setShareRoles(visibility === 'role' ? roles : []);
    toast.success(t('share.updated', 'Sharing updated.'));
  };

  const submitSave = async () => {
    const name = saveName.trim();
    if (!name || saving) return;
    setSaving(true);
    try {
      const created = await createSavedReport({
        name,
        description: saveDescription.trim() || undefined,
        domain: template.domain,
        template_id: template.id,
        columns: visibleColumnKeys,
        filters: currentFilters(),
        visibility: publishTenant ? 'tenant' : 'private',
      });
      setSaveOpen(false);
      toast.success(t('saved.created', 'Report saved. Find it under "My reports".'));
      navigate(`${base}/saved/${created.id}`);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, apiErrorMessage(e, t('saved.createFailed', 'Could not save this report.'))));
    } finally {
      setSaving(false);
    }
  };

  const [updating, setUpdating] = useState(false);
  const saveChanges = async () => {
    if (!saved || updating) return;
    setUpdating(true);
    try {
      await updateSavedReport(saved.id, {
        name: saved.name,
        description: saved.description ?? undefined,
        domain: template.domain,
        template_id: template.id,
        columns: visibleColumnKeys,
        filters: currentFilters(),
        visibility: shareVisibility,
        shared_roles: shareRoles,
        status: saved.status,
      });
      toast.success(t('saved.updated', 'Saved report updated.'));
    } catch (e: any) {
      toast.error(
        e?.response?.status === 403
          ? t('saved.updateForbidden', 'You can only edit your own reports.')
          : apiErrorMessage(e, t('saved.updateFailed', 'Could not update this saved report.')),
      );
    } finally {
      setUpdating(false);
    }
  };

  const duplicateSaved = async () => {
    if (!saved) return;
    try {
      const copy = await duplicateSavedReport(saved.id);
      toast.success(t('saved.duplicated', 'Report duplicated.'));
      navigate(`${base}/saved/${copy.id}`);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, apiErrorMessage(e, t('saved.duplicateFailed', 'Could not duplicate this report.'))));
    }
  };

  const archiveSaved = async () => {
    if (!saved) return;
    const ok = await confirm({
      title: t('saved.archiveTitle', 'Archive saved report?'),
      message: t('saved.archiveMessage', '"{{name}}" will be removed from My reports. The underlying template stays available.', {
        name: saved.name,
      }),
      confirmLabel: t('saved.archive', 'Archive'),
      danger: true,
    });
    if (!ok) return;
    try {
      await archiveSavedReport(saved.id);
      toast.success(t('saved.archived', 'Saved report archived.'));
      navigate(base);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, apiErrorMessage(e, t('saved.archiveFailed', 'Could not archive this report.'))));
    }
  };

  // ---- Passthrough templates ------------------------------------------------------

  if (template.kind === 'passthrough') {
    return (
      <div className="space-y-6">
        <BackLink label={t('viewer.back', 'Back to reports')} onClick={() => navigate(base)} />
        <Card>
          <CardHeader>
            <CardTitle>{saved ? saved.name : tplName(template)}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t(`templates.${template.id}.description`, template.description)}
            </p>
            <Button onClick={() => navigate(`${tenantRoot}/${template.route ?? ''}`)}>
              <ExternalLink className="w-4 h-4 mr-2" />
              {t('open', 'Open')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- CSV export -------------------------------------------------------------

  const exportCsv = () => {
    if (!result) return;
    const generatedBy = user ? `${user.name || ''}${user.email ? ` <${user.email}>` : ''}`.trim() : 'unknown';
    const filterParts: string[] = [];
    if (needsRun && runId) {
      const run = runs.find((r) => r.id === runId);
      filterParts.push(`run=${run ? `${run.periodLabel} (${run.state})` : runId}`);
    }
    if (hasDateRange) filterParts.push(`from=${from || '—'}`, `to=${to || '—'}`);
    if (hasStatus) filterParts.push(`status=${status || 'all'}`);

    const meta: (string | number | null)[][] = [
      ['Report', saved ? saved.name : tplName(template)],
      ['Generated at', new Date().toISOString()],
      ['Generated by', generatedBy],
      ['Filters used', filterParts.length ? filterParts.join('; ') : 'none'],
      ['Source', template.kind === 'snapshot' ? 'locked payroll snapshot' : 'live data'],
    ];
    if (saved) meta.push(['Saved report from template', tplName(template)]);
    if (template.sensitive) meta.push(['Confidential', 'This report contains sensitive data — handle accordingly']);

    const headers = visibleColumns.map((c) => c.label);
    const rows = result.rows.map((row) => visibleColumns.map((c) => row[c.key] ?? ''));
    const csv = `${toCSV([], meta)}\r\n\r\n${toCSV(headers, rows)}`;
    downloadCsv(`${saved ? saved.id : template.id}-${isoDay(new Date())}.csv`, csv);
  };

  // ---- Server export for curated engine templates (P4) ------------------------
  // Same preset as the preview, full rows (server cap 5000), same viewer
  // scope — the file cannot show anything the screen would not.
  const presetExport = async (format: ReportExportFormat) => {
    if (!template.preset || presetExporting) return;
    setPresetExporting(true);
    try {
      const filters: Record<string, string> = { ...(template.preset.filters ?? {}) };
      if (hasDateRange && from) filters.from = from;
      if (hasDateRange && to) filters.to = to;
      const res = await exportReport({
        domain: template.preset.domain, columns: template.preset.columns, filters,
        group_by: template.preset.group_by, aggregations: template.preset.aggregations,
        format, title: tplName(template),
      });
      openReportDownload(res.download_url);
      let msg = t('export.ready', 'Export ready — {{count}} row(s).', { count: res.row_count });
      if (res.truncated) msg += ` ${t('export.truncatedNote', 'Truncated at 5000 rows.')}`;
      toast.success(msg, { duration: 6000 });
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, apiErrorMessage(e, t('export.failed', 'Could not export this report.'))));
    } finally {
      setPresetExporting(false);
    }
  };

  // ---- Render -------------------------------------------------------------------

  const nonDraftMissing = needsRun && !runsLoading && runs.length === 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <BackLink label={t('viewer.back', 'Back to reports')} onClick={() => navigate(base)} />
          <h1 className="text-2xl font-bold text-foreground mt-2">{saved ? saved.name : tplName(template)}</h1>
          {saved ? (
            <>
              {saved.description && <p className="text-slate-600 mt-1 max-w-2xl">{saved.description}</p>}
              <p className="text-sm text-muted-foreground mt-1">
                {t('saved.editedFrom', 'Edited from “{{template}}”', { template: tplName(template) })}
              </p>
            </>
          ) : (
            <p className="text-slate-600 mt-1 max-w-2xl">
              {t(`templates.${template.id}.description`, template.description)}
            </p>
          )}
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline" className="text-[11px]">
              {template.kind === 'snapshot'
                ? t('viewer.sourceSnapshot', 'Source: locked payroll snapshot')
                : t('viewer.sourceLive', 'Source: live data')}
            </Badge>
            {saved && <SharingBadge visibility={shareVisibility} roles={shareRoles} />}
            {template.sensitive && (
              <Badge className="bg-warning-soft text-warning-strong border border-transparent hover:bg-warning-soft text-[11px]">
                <ShieldAlert className="w-3 h-3 mr-1" />
                {t('confidential', 'Confidential')}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {saved ? (
            <>
              {canEditSaved && (
                <Button variant="outline" onClick={saveChanges} disabled={updating}>
                  {updating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {t('saved.saveChanges', 'Save changes')}
                </Button>
              )}
              {canEditSaved && canShare && (
                <Button variant="outline" onClick={() => setShareOpen(true)}>
                  <Share2 className="w-4 h-4 mr-2" />
                  {t('share.share', 'Share')}
                </Button>
              )}
              {canEditSaved && canSchedule && saved && (
                <Button variant="outline" onClick={() => setScheduleOpen(true)}>
                  <CalendarClock className="w-4 h-4 mr-2" />
                  {t('schedule.button', 'Schedule')}
                </Button>
              )}
              <Button variant="outline" onClick={duplicateSaved}>
                <Copy className="w-4 h-4 mr-2" />
                {t('saved.duplicate', 'Duplicate')}
              </Button>
              {canEditSaved && (
                <Button
                  variant="outline"
                  className="text-red-600 hover:text-red-700"
                  onClick={archiveSaved}
                >
                  <Archive className="w-4 h-4 mr-2" />
                  {t('saved.archive', 'Archive')}
                </Button>
              )}
            </>
          ) : (
            canCreate && (
              <Button
                variant="outline"
                onClick={() => {
                  setSaveName(tplName(template));
                  setSaveDescription('');
                  setPublishTenant(false);
                  setSaveOpen(true);
                }}
              >
                <Save className="w-4 h-4 mr-2" />
                {t('saved.saveReport', 'Save report')}
              </Button>
            )
          )}
          <Button onClick={exportCsv} disabled={!result || result.rows.length === 0}>
            <Download className="w-4 h-4 mr-2" />
            {t('viewer.exportCsv', 'Export CSV')}
          </Button>
          {canServerExport && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={presetExporting}>
                  {presetExporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  {t('export.export', 'Export')}
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => presetExport('xlsx')}>{t('export.xlsx', 'Excel (.xlsx)')}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => presetExport('csv')}>{t('export.csvFull', 'CSV (full)')}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {template.sensitive && (
        <div className="flex items-start gap-2 rounded-lg border border-warning bg-warning-soft px-4 py-3 text-sm text-warning-strong">
          <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            {t(
              'viewer.confidentialBanner',
              'This report contains confidential data. Only share it with people who are authorized to see it.',
            )}
          </span>
        </div>
      )}

      {(needsRun || hasDateRange || hasStatus) && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-end gap-4">
              {needsRun && (
                <div className="space-y-1.5">
                  <Label>{t('viewer.payrollRun', 'Payroll run')}</Label>
                  <Select value={runId} onValueChange={setRunId} disabled={runsLoading || runs.length === 0}>
                    <SelectTrigger className="w-64">
                      <SelectValue
                        placeholder={
                          runsLoading ? t('viewer.loadingRuns', 'Loading runs…') : t('viewer.selectRun', 'Select a run')
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {runs.map((r) => (
                        <SelectItem key={r.id} value={r.id}>
                          {r.periodLabel} — {r.state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {hasDateRange && (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="report-from">{t('viewer.from', 'From')}</Label>
                    <Input
                      id="report-from"
                      type="date"
                      value={from}
                      onChange={(e) => setFrom(e.target.value)}
                      className="w-44"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="report-to">{t('viewer.to', 'To')}</Label>
                    <Input
                      id="report-to"
                      type="date"
                      value={to}
                      onChange={(e) => setTo(e.target.value)}
                      className="w-44"
                    />
                  </div>
                </>
              )}
              {hasStatus && (
                <div className="space-y-1.5">
                  <Label>{t('viewer.status', 'Status')}</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="w-44">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(template.statusOptions ?? []).map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          {loading || runsLoading ? (
            <div className="py-16 flex items-center justify-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              {t('viewer.loading', 'Running report…')}
            </div>
          ) : nonDraftMissing ? (
            <div className="py-16 text-center text-muted-foreground">
              {t('viewer.noRuns', 'No payroll runs yet — create and calculate a run first.')}
            </div>
          ) : error ? (
            <div className="py-16 text-center">
              <p className="text-danger font-medium mb-2">{t('viewer.error', 'Could not run this report')}</p>
              <p className="text-sm text-muted-foreground mb-4">{error}</p>
              <Button variant="outline" onClick={() => runReport()}>
                {t('viewer.retry', 'Try again')}
              </Button>
            </div>
          ) : !result || result.rows.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              <FileBarChart2 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              {t('viewer.empty', 'No rows for the selected filters.')}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-muted-foreground">
                  {t('viewer.rowCount', '{{count}} row(s)', { count: result.rows.length })}
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm">
                      <SlidersHorizontal className="w-4 h-4 mr-2" />
                      {t('viewer.columns', 'Columns')}
                      {selectedCols && visibleColumns.length < allColumns.length && (
                        <span className="ml-1.5 text-xs text-muted-foreground">
                          {visibleColumns.length}/{allColumns.length}
                        </span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-64 p-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                      {t('viewer.visibleColumns', 'Visible columns')}
                    </p>
                    <div className="space-y-2 max-h-72 overflow-y-auto">
                      {allColumns.map((c) => {
                        const checked = !selectedCols || selectedCols.includes(c.key);
                        return (
                          <label key={c.key} className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox checked={checked} onCheckedChange={() => toggleColumn(c.key)} />
                            <span>{c.label}</span>
                          </label>
                        );
                      })}
                    </div>
                    {selectedCols && visibleColumns.length < allColumns.length && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full mt-2"
                        onClick={() => setSelectedCols(null)}
                      >
                        {t('viewer.showAllColumns', 'Show all columns')}
                      </Button>
                    )}
                  </PopoverContent>
                </Popover>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {visibleColumns.map((c) => (
                        <TableHead key={c.key}>{c.label}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.rows.map((row, i) => (
                      <TableRow key={i}>
                        {visibleColumns.map((c) => (
                          <TableCell key={c.key}>{row[c.key] ?? ''}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Save-report dialog (template mode, reports:create) */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('saved.dialogTitle', 'Save report')}</DialogTitle>
            <DialogDescription>
              {t(
                'saved.dialogBody',
                'Saves this report with its current filters and column selection so you can re-run it from "My reports".',
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="saved-report-name">{t('saved.name', 'Name')}</Label>
              <Input
                id="saved-report-name"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder={t('saved.namePlaceholder', 'e.g. Monthly new hires — HQ')}
                maxLength={160}
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="saved-report-description">
                {t('saved.description', 'Description')}{' '}
                <span className="text-muted-foreground font-normal">{t('optional', '(optional)')}</span>
              </Label>
              <Textarea
                id="saved-report-description"
                value={saveDescription}
                onChange={(e) => setSaveDescription(e.target.value)}
                rows={3}
                placeholder={t('saved.descriptionPlaceholder', 'What this report is for…')}
              />
            </div>
            {isTenantOwner && (
              <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
                <div className="pr-3">
                  <p className="text-sm font-medium text-foreground">
                    {t('saved.publishTenant', 'Publish to whole company')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('saved.publishTenantHint', 'Everyone with reports access sees it; otherwise it stays private to you.')}
                  </p>
                </div>
                <Switch checked={publishTenant} onCheckedChange={setPublishTenant} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)} disabled={saving}>
              {t('cancel', 'Cancel')}
            </Button>
            <Button onClick={submitSave} disabled={!saveName.trim() || saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {t('saved.save', 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share dialog (saved mode, reports:share) */}
      {saved && (
        <ShareReportDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          visibility={shareVisibility}
          sharedRoles={shareRoles}
          canPublishTenant={isTenantOwner}
          onSubmit={submitShare}
        />
      )}
      {saved && (
        <ScheduleReportDialog
          open={scheduleOpen}
          onOpenChange={setScheduleOpen}
          definitionId={saved.id}
          reportName={saved.name}
        />
      )}
    </div>
  );
};

// ---- Engine viewer (R4) -----------------------------------------------------------
//
// Saved definitions WITHOUT a template_id were created in the guided Report
// Builder. They have no frontend registry template: the server-side field
// catalog (getDomainFields) provides column labels/types + filter controls,
// and execution goes through runSavedReport — the engine runs the SAVED
// column list under the viewer's own permissions (cap 5000 rows) and stamps
// last_run_at, so no client-side `ran` call is needed.

const EngineViewerBody: React.FC<{ saved: SavedReport; base: string }> = ({ saved, base }) => {
  const { t } = useTranslation('reports');
  const navigate = useNavigate();
  const user = useAppSelector((s) => s.auth.user);
  const canExport = useHasPermission('reports:export');
  const canShare = useHasPermission('reports:share');
  const { isTenantOwner } = useRoleAccess();
  const confirm = useConfirm();

  const ownsSaved = !!user?.id && String(saved.owner_user_id) === String(user.id);
  const canEditSaved = ownsSaved || isTenantOwner;

  /** Stored grouping — the server applies it on every run; drives the chart. */
  const savedGroupBy = saved.group_by ?? [];

  // ---- Domain catalog (labels, types, filter defs) ------------------------------
  const [meta, setMeta] = useState<ReportDomainFields | null>(null);
  const [metaLoading, setMetaLoading] = useState(true);
  const [metaForbidden, setMetaForbidden] = useState(false);
  const [metaError, setMetaError] = useState('');

  useEffect(() => {
    let active = true;
    getDomainFields(saved.domain)
      // Normalize null arrays to [] (filter-less domains like Leave Balances).
      .then((m) => active && setMeta({
        ...m,
        fields: Array.isArray(m?.fields) ? m.fields : [],
        filters: Array.isArray(m?.filters) ? m.filters : [],
      }))
      .catch((e: any) => {
        if (!active) return;
        if (e?.response?.status === 403) setMetaForbidden(true);
        else setMetaError(apiErrorMessage(e, t('engine.metaFailed', "Could not load this report's field catalog.")));
      })
      .finally(() => active && setMetaLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved.domain]);

  // ---- Filters (rehydrated from the saved definition) ----------------------------
  const [filterValues, setFilterValues] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(saved.filters ?? {})) {
      if (typeof v === 'string' && v) out[k] = v;
    }
    return out;
  });
  const setFilter = (key: string, value: string) =>
    setFilterValues((cur) => ({ ...cur, [key]: value }));

  // Payroll-run picker (only when the domain declares a `run` filter). A saved
  // run id wins when it still exists; otherwise default to the newest non-draft.
  const runFilterKey = useMemo(
    () => meta?.filters?.find((f) => f.type === 'run')?.key ?? '',
    [meta],
  );
  const [runs, setRuns] = useState<PayrollRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);

  useEffect(() => {
    if (!runFilterKey) return;
    let active = true;
    setRunsLoading(true);
    payrollApi
      .listRuns()
      .then((list) => {
        if (!active) return;
        const nonDraft = list.filter((r) => r.state !== 'draft');
        setRuns(nonDraft);
        setFilterValues((cur) => {
          if (cur[runFilterKey] && nonDraft.some((r) => r.id === cur[runFilterKey])) return cur;
          return nonDraft[0] ? { ...cur, [runFilterKey]: nonDraft[0].id } : cur;
        });
      })
      .catch(() => active && setRuns([]))
      .finally(() => active && setRunsLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runFilterKey]);

  // ---- Execution ------------------------------------------------------------------
  const [result, setResult] = useState<ReportEngineResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Wait for the run picker before the first execution of run-filtered domains.
  const waitingForRun = !!runFilterKey && !filterValues[runFilterKey] && (runsLoading || runs.length > 0);
  const noRunsAvailable = !!runFilterKey && !runsLoading && runs.length === 0 && !filterValues[runFilterKey];

  const execute = useCallback(() => {
    if (!meta || waitingForRun || noRunsAvailable) return;
    let active = true;
    setLoading(true);
    setError('');
    runSavedReport(saved.id, cleanFilters(filterValues))
      .then((res) => active && setResult(res))
      .catch((e: any) => {
        if (!active) return;
        setResult(null);
        setError(apiErrorMessage(e, t('engine.runFailed', 'Could not run this report.')));
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta, saved.id, filterValues, waitingForRun, noRunsAvailable]);

  useEffect(() => {
    const cancel = execute();
    return cancel;
  }, [execute]);

  // ---- Column visibility (subset of the saved columns, like template mode) --------
  const allColumns = result?.columns ?? [];
  const [selectedCols, setSelectedCols] = useState<string[] | null>(null);
  const visibleColumns = useMemo(
    () => (selectedCols ? allColumns.filter((c) => selectedCols.includes(c.key)) : allColumns),
    [allColumns, selectedCols],
  );
  const toggleColumn = (key: string) => {
    const current = selectedCols ?? allColumns.map((c) => c.key);
    const next = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
    if (allColumns.length > 0 && allColumns.every((c) => !next.includes(c.key))) return;
    setSelectedCols(next);
  };
  const visibleColumnKeys = visibleColumns.map((c) => c.key);

  // ---- Sharing (R6) — persisted state lives here so the badge updates live. -------
  const [shareOpen, setShareOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const canSchedule = useHasPermission('reports:schedule');
  const [shareVisibility, setShareVisibility] = useState<SavedReportVisibility>(saved.visibility);
  const [shareRoles, setShareRoles] = useState<string[]>(saved.shared_roles ?? []);

  /** Persists sharing only — rethrows so the dialog can surface 'share_blocked'. */
  const submitShare = async (visibility: SavedReportVisibility, roles: string[]) => {
    await updateSavedReport(saved.id, {
      name: saved.name,
      description: saved.description ?? undefined,
      domain: saved.domain,
      columns: saved.columns,
      filters: saved.filters,
      group_by: saved.group_by ?? undefined,
      aggregations: saved.aggregations ?? undefined,
      visibility,
      shared_roles: visibility === 'role' ? roles : [],
      status: saved.status,
    });
    setShareVisibility(visibility);
    setShareRoles(visibility === 'role' ? roles : []);
    toast.success(t('share.updated', 'Sharing updated.'));
  };

  // ---- Saved-mode actions ------------------------------------------------------------
  const [updating, setUpdating] = useState(false);
  const saveChanges = async () => {
    if (updating) return;
    setUpdating(true);
    try {
      // NO template_id — this stays an engine-backed definition. Grouping and
      // sharing are passed through so a full PUT doesn't wipe them.
      await updateSavedReport(saved.id, {
        name: saved.name,
        description: saved.description ?? undefined,
        domain: saved.domain,
        // Grouped results return GENERATED column keys (count, sum_<field>…) —
        // never persist those as the definition's catalog columns.
        columns:
          savedGroupBy.length > 0 || (saved.aggregations?.length ?? 0) > 0
            ? saved.columns
            : visibleColumnKeys.length > 0
              ? visibleColumnKeys
              : saved.columns,
        filters: cleanFilters(filterValues),
        group_by: saved.group_by ?? undefined,
        aggregations: saved.aggregations ?? undefined,
        visibility: shareVisibility,
        shared_roles: shareRoles,
        status: saved.status,
      });
      toast.success(t('saved.updated', 'Saved report updated.'));
    } catch (e: any) {
      toast.error(
        e?.response?.status === 403
          ? t('saved.updateForbidden', 'You can only edit your own reports.')
          : apiErrorMessage(e, t('saved.updateFailed', 'Could not update this saved report.')),
      );
    } finally {
      setUpdating(false);
    }
  };

  const duplicateSaved = async () => {
    try {
      const copy = await duplicateSavedReport(saved.id);
      toast.success(t('saved.duplicated', 'Report duplicated.'));
      navigate(`${base}/saved/${copy.id}`);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, apiErrorMessage(e, t('saved.duplicateFailed', 'Could not duplicate this report.'))));
    }
  };

  const archiveSaved = async () => {
    const ok = await confirm({
      title: t('saved.archiveTitle', 'Archive saved report?'),
      message: t('saved.archiveEngineMessage', '"{{name}}" will be removed from My reports.', { name: saved.name }),
      confirmLabel: t('saved.archive', 'Archive'),
      danger: true,
    });
    if (!ok) return;
    try {
      await archiveSavedReport(saved.id);
      toast.success(t('saved.archived', 'Saved report archived.'));
      navigate(base);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, apiErrorMessage(e, t('saved.archiveFailed', 'Could not archive this report.'))));
    }
  };

  // ---- CSV export --------------------------------------------------------------------
  const exportCsv = () => {
    if (!result) return;
    const generatedBy = user ? `${user.name || ''}${user.email ? ` <${user.email}>` : ''}`.trim() : 'unknown';
    const clean = cleanFilters(filterValues);
    const filterParts = Object.entries(clean).map(([k, v]) => {
      if (runFilterKey && k === runFilterKey) {
        const run = runs.find((r) => r.id === v);
        return `run=${run ? `${run.periodLabel} (${run.state})` : v}`;
      }
      return `${k}=${v}`;
    });
    const meta_: (string | number | null)[][] = [
      ['Report', saved.name],
      ['Generated at', new Date().toISOString()],
      ['Generated by', generatedBy],
      ['Filters used', filterParts.length ? filterParts.join('; ') : 'none'],
      ['Source', result.source === 'snapshot' ? 'locked payroll snapshot' : 'live data'],
    ];
    const headers = visibleColumns.map((c) => c.label);
    const rows = result.rows.map((row) => visibleColumns.map((c) => formatCell(row[c.key], c.type)));
    const csv = `${toCSV([], meta_)}\r\n\r\n${toCSV(headers, rows)}`;
    downloadCsv(`${saved.id}-${isoDay(new Date())}.csv`, csv);
  };

  // ---- Server export (R5) — the file (metadata block included) is built server-side
  // from the STORED definition (columns/filters/grouping), capped at 5000 rows.
  const [exporting, setExporting] = useState(false);

  const serverExport = async (format: ReportExportFormat) => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await exportReport({ definition_id: saved.id, format, title: saved.name });
      openReportDownload(res.download_url);
      let msg = t('export.ready', 'Export ready — {{count}} row(s).', { count: res.row_count });
      if (res.truncated) msg += ` ${t('export.truncatedNote', 'Truncated at 5000 rows.')}`;
      if (res.confidential)
        msg += ` ${t('export.confidentialNote', 'Contains confidential data — handle accordingly.')}`;
      toast.success(msg, { duration: 6000 });
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, apiErrorMessage(e, t('export.failed', 'Could not export this report.'))));
    } finally {
      setExporting(false);
    }
  };

  // ---- States ---------------------------------------------------------------------------

  if (metaLoading) {
    return (
      <div className="py-16 flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        {t('saved.loading', 'Loading saved report…')}
      </div>
    );
  }

  if (metaForbidden) {
    return (
      <NotFoundState
        title={t('engine.noAccessTitle', 'No access to this report’s data')}
        body={t(
          'engine.noAccessBody',
          "You no longer have access to this report's data. Ask your administrator if you believe this is a mistake.",
        )}
        backLabel={t('viewer.back', 'Back to reports')}
        onBack={() => navigate(base)}
      />
    );
  }

  if (metaError || !meta) {
    return (
      <NotFoundState
        title={t('saved.notFound', 'Saved report unavailable')}
        body={metaError || t('saved.loadFailed', 'Could not load this saved report.')}
        backLabel={t('viewer.back', 'Back to reports')}
        onBack={() => navigate(base)}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <BackLink label={t('viewer.back', 'Back to reports')} onClick={() => navigate(base)} />
          <h1 className="text-2xl font-bold text-foreground mt-2">{saved.name}</h1>
          {saved.description && <p className="text-slate-600 mt-1 max-w-2xl">{saved.description}</p>}
          <p className="text-sm text-muted-foreground mt-1">
            {t('engine.builtWith', 'Custom report — {{domain}}', { domain: meta.label })}
          </p>
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline" className="text-[11px]">
              {meta.source === 'snapshot'
                ? t('viewer.sourceSnapshot', 'Source: locked payroll snapshot')
                : t('viewer.sourceLive', 'Source: live data')}
            </Badge>
            <SharingBadge visibility={shareVisibility} roles={shareRoles} />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canEditSaved && (
            <Button variant="outline" onClick={saveChanges} disabled={updating}>
              {updating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {t('saved.saveChanges', 'Save changes')}
            </Button>
          )}
          {canEditSaved && canShare && (
            <Button variant="outline" onClick={() => setShareOpen(true)}>
              <Share2 className="w-4 h-4 mr-2" />
              {t('share.share', 'Share')}
            </Button>
          )}
          {canEditSaved && canSchedule && saved && (
            <Button variant="outline" onClick={() => setScheduleOpen(true)}>
              <CalendarClock className="w-4 h-4 mr-2" />
              {t('schedule.button', 'Schedule')}
            </Button>
          )}
          <Button variant="outline" onClick={duplicateSaved}>
            <Copy className="w-4 h-4 mr-2" />
            {t('saved.duplicate', 'Duplicate')}
          </Button>
          {canEditSaved && (
            <Button variant="outline" className="text-red-600 hover:text-red-700" onClick={archiveSaved}>
              <Archive className="w-4 h-4 mr-2" />
              {t('saved.archive', 'Archive')}
            </Button>
          )}
          <Button variant="outline" onClick={exportCsv} disabled={!result || result.rows.length === 0}>
            <Download className="w-4 h-4 mr-2" />
            {t('export.previewCsv', 'CSV (preview rows)')}
          </Button>
          {canExport && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button disabled={exporting}>
                  {exporting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  {t('export.export', 'Export')}
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => serverExport('xlsx')}>
                  {t('export.xlsx', 'Excel (.xlsx)')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => serverExport('csv')}>
                  {t('export.csvFull', 'CSV (full)')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {meta.filters.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <EngineFilterBar
              filters={meta.filters}
              values={filterValues}
              onChange={setFilter}
              runs={runs}
              runsLoading={runsLoading}
            />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          {loading || runsLoading || waitingForRun ? (
            <div className="py-16 flex items-center justify-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              {t('viewer.loading', 'Running report…')}
            </div>
          ) : noRunsAvailable ? (
            <div className="py-16 text-center text-muted-foreground">
              {t('viewer.noRuns', 'No payroll runs yet — create and calculate a run first.')}
            </div>
          ) : error ? (
            <div className="py-16 text-center">
              <p className="text-danger font-medium mb-2">{t('viewer.error', 'Could not run this report')}</p>
              <p className="text-sm text-muted-foreground mb-4">{error}</p>
              <Button variant="outline" onClick={() => execute()}>
                {t('viewer.retry', 'Try again')}
              </Button>
            </div>
          ) : !result || result.rows.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              <FileBarChart2 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              {t('viewer.empty', 'No rows for the selected filters.')}
              <ReportScopeEmptyReason scope={result?.scope} />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-muted-foreground">
                  {t('viewer.rowCount', '{{count}} row(s)', { count: result.rows.length })}
                  {result.truncated && (
                    <span className="ml-2 text-warning">
                      {t('engine.truncated', '— showing first {{count}} of {{total}}', {
                        count: result.rows.length,
                        total: result.total,
                      })}
                    </span>
                  )}
                  <ReportScopeNotice scope={result.scope} />
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm">
                      <SlidersHorizontal className="w-4 h-4 mr-2" />
                      {t('viewer.columns', 'Columns')}
                      {selectedCols && visibleColumns.length < allColumns.length && (
                        <span className="ml-1.5 text-xs text-muted-foreground">
                          {visibleColumns.length}/{allColumns.length}
                        </span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-64 p-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                      {t('viewer.visibleColumns', 'Visible columns')}
                    </p>
                    <div className="space-y-2 max-h-72 overflow-y-auto">
                      {allColumns.map((c) => {
                        const checked = !selectedCols || selectedCols.includes(c.key);
                        return (
                          <label key={c.key} className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox checked={checked} onCheckedChange={() => toggleColumn(c.key)} />
                            <span>{c.label}</span>
                          </label>
                        );
                      })}
                    </div>
                    {selectedCols && visibleColumns.length < allColumns.length && (
                      <Button variant="ghost" size="sm" className="w-full mt-2" onClick={() => setSelectedCols(null)}>
                        {t('viewer.showAllColumns', 'Show all columns')}
                      </Button>
                    )}
                  </PopoverContent>
                </Popover>
              </div>
              <EngineResultChart result={result} groupBy={savedGroupBy} />
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {visibleColumns.map((c) => (
                        <TableHead key={c.key}>{c.label}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.rows.map((row, i) => (
                      <TableRow key={i}>
                        {visibleColumns.map((c) => (
                          <TableCell key={c.key}>{formatCell(row[c.key], c.type)}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Share dialog (reports:share) */}
      <ShareReportDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        visibility={shareVisibility}
        sharedRoles={shareRoles}
        canPublishTenant={isTenantOwner}
        onSubmit={submitShare}
      />
      <ScheduleReportDialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        definitionId={saved.id}
        reportName={saved.name}
      />
    </div>
  );
};

// ---- Sharing badge (R6) — current sharing state, rendered near the title. ----------

const SharingBadge: React.FC<{ visibility: SavedReportVisibility; roles: string[] }> = ({
  visibility,
  roles,
}) => {
  const { t } = useTranslation('reports');
  if (visibility === 'tenant') {
    return (
      <Badge className="bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-50 text-[11px]">
        {t('saved.companyWide', 'Company-wide')}
      </Badge>
    );
  }
  if (visibility === 'role') {
    return (
      <Badge className="bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-50 text-[11px]">
        {t('share.sharedWith', 'Shared: {{roles}}', {
          roles: roles.map(shareRoleLabel).join(', '),
        })}
      </Badge>
    );
  }
  if (visibility === 'users') {
    return (
      <Badge className="bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-50 text-[11px]">
        {t('share.sharedUsers', 'Shared: specific people')}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-[11px] text-muted-foreground">
      {t('share.privateBadge', 'Private')}
    </Badge>
  );
};

const BackLink: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
  >
    <ArrowLeft className="w-4 h-4" />
    {label}
  </button>
);

const NotFoundState: React.FC<{ title: string; body: string; backLabel: string; onBack: () => void }> = ({
  title,
  body,
  backLabel,
  onBack,
}) => (
  <div className="text-center py-16">
    <FileBarChart2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
    <h2 className="text-xl font-semibold text-foreground mb-2">{title}</h2>
    <p className="text-slate-600 mb-6">{body}</p>
    <Button variant="outline" onClick={onBack}>
      <ArrowLeft className="w-4 h-4 mr-2" />
      {backLabel}
    </Button>
  </div>
);

export default ReportViewerPage;
